// Efekty sukcesu i porazki rysowane na plotnie trasy po zakonczeniu proby.
//
// Wszystko jest deterministyczne (wlasny generator z stalym ziarnem), wiec ten
// sam efekt wyglada tak samo po restarcie - bez zaleznosci od FPS czy losowosci.

import type { Camera } from './camera';

const GOLD = '#ffd479';
const OK = '#6bd18a';
const BAD = '#ff8a7a';

export type Outcome = 'won' | 'failed';

export interface OutcomeEffectOptions {
  outcome: Outcome;
  /** Srodek efektu w metrach (np. srodek robota po zakonczeniu proby). */
  x: number;
  y: number;
  /** Czas od zakonczenia proby (sekundy). */
  t: number;
}

/** Jak dlugo trwa efekt danego wyniku (sekundy). */
export function outcomeEffectDuration(outcome: Outcome): number {
  return outcome === 'won' ? 2.4 : 1.5;
}

/** Wstrzas ekranu przy porazce (w pikselach). Sukces = brak. */
export function outcomeShake(outcome: Outcome, t: number): { x: number; y: number } {
  if (outcome !== 'failed') return { x: 0, y: 0 };
  const decay = Math.max(0, 1 - t / 0.6);
  if (decay <= 0) return { x: 0, y: 0 };
  const amplitude = 9 * decay;
  return { x: Math.sin(t * 55) * amplitude, y: Math.cos(t * 47) * amplitude };
}

/** Deterministyczny generator pseudolosowy (LCG) - ten sam efekt co klatke. */
function stepper(seed: number): () => number {
  let state = seed >>> 0;
  return () => {
    state = (state * 1664525 + 1013904223) >>> 0;
    return state / 0xffffffff;
  };
}

function drawBanner(
  ctx: CanvasRenderingContext2D,
  camera: Camera,
  center: { x: number; y: number },
  text: string,
  color: string,
  t: number,
  delay: number,
): void {
  if (t < delay) return;
  const local = t - delay;
  const appear = Math.min(1, local / 0.22);
  const fade = local > 1.2 ? Math.max(0, 1 - (local - 1.2) / 0.7) : 1;
  const alpha = appear * fade;
  if (alpha <= 0) return;

  const y = Math.min(camera.height - 24, Math.max(24, center.y - camera.scale * 0.6));
  const size = 30 * (0.7 + 0.3 * appear);

  ctx.save();
  ctx.globalAlpha = alpha;
  ctx.font = `bold ${size.toFixed(1)}px system-ui, sans-serif`;
  ctx.textAlign = 'center';
  ctx.textBaseline = 'middle';
  ctx.lineWidth = 4;
  ctx.strokeStyle = 'rgba(0, 0, 0, 0.55)';
  ctx.strokeText(text, center.x, y);
  ctx.fillStyle = color;
  ctx.fillText(text, center.x, y);
  ctx.restore();
}

/** Sukces: rozchodzace sie pierscienie, konfetti i napis. */
function drawSuccess(
  ctx: CanvasRenderingContext2D,
  camera: Camera,
  options: OutcomeEffectOptions,
): void {
  const t = options.t;
  const center = camera.worldToScreen(options.x, options.y);

  // Pierscienie.
  ctx.save();
  for (let i = 0; i < 3; i += 1) {
    const local = t - i * 0.16;
    if (local < 0 || local > 0.9) continue;
    const radius = (0.3 + local * 1.8) * camera.scale;
    const alpha = (1 - local / 0.9) * 0.8;
    ctx.strokeStyle = `rgba(255, 212, 121, ${alpha.toFixed(3)})`;
    ctx.lineWidth = 3;
    ctx.beginPath();
    ctx.arc(center.x, center.y, radius, 0, Math.PI * 2);
    ctx.stroke();
  }
  ctx.restore();

  // Konfetti (deterministyczne - stale trajektorie).
  const random = stepper(0x5eed);
  const palette = [GOLD, OK, '#7fb2ff', BAD, '#ffffff'];
  const life = 1.7;
  ctx.save();
  for (let i = 0; i < 32; i += 1) {
    const angle = random() * Math.PI * 2;
    const speed = 1.4 + random() * 2.4;
    const size = 3 + random() * 3;
    if (t > life) continue;

    const vx = Math.cos(angle) * speed;
    const vy = Math.abs(Math.sin(angle)) * speed + 2.4;
    const point = camera.worldToScreen(options.x + vx * t, options.y + vy * t - 4.9 * t * t);

    ctx.globalAlpha = Math.max(0, 1 - t / life);
    ctx.fillStyle = palette[i % palette.length] ?? GOLD;
    ctx.beginPath();
    ctx.arc(point.x, point.y, size, 0, Math.PI * 2);
    ctx.fill();
  }
  ctx.restore();

  drawBanner(ctx, camera, center, 'SUKCES', OK, t, 0.2);
}

/** Porazka: czerwony blysk, kleby dymu i napis. */
function drawFailure(
  ctx: CanvasRenderingContext2D,
  camera: Camera,
  options: OutcomeEffectOptions,
): void {
  const t = options.t;
  const center = camera.worldToScreen(options.x, options.y);

  const flash = Math.max(0, 1 - t / 0.5) * 0.35;
  if (flash > 0) {
    ctx.save();
    ctx.fillStyle = `rgba(255, 90, 70, ${flash.toFixed(3)})`;
    ctx.fillRect(0, 0, camera.width, camera.height);
    ctx.restore();
  }

  const random = stepper(0xbad5);
  const life = 1.2;
  ctx.save();
  for (let i = 0; i < 12; i += 1) {
    const angle = random() * Math.PI * 2;
    const speed = 0.5 + random() * 1.2;
    const size = 6 + random() * 10;
    if (t > life) continue;

    const px = options.x + Math.cos(angle) * speed * t;
    const py = options.y + 0.4 * t + Math.sin(angle) * 0.5 * t;
    const point = camera.worldToScreen(px, py);

    ctx.globalAlpha = Math.max(0, 1 - t / life) * 0.5;
    ctx.fillStyle = '#7a8698';
    ctx.beginPath();
    ctx.arc(point.x, point.y, size * (0.6 + t), 0, Math.PI * 2);
    ctx.fill();
  }
  ctx.restore();

  drawBanner(ctx, camera, center, 'PORAŻKA', BAD, t, 0.05);
}

/** Rysuje efekt wyniku proby. */
export function drawOutcomeEffect(
  ctx: CanvasRenderingContext2D,
  camera: Camera,
  options: OutcomeEffectOptions,
): void {
  if (options.outcome === 'won') drawSuccess(ctx, camera, options);
  else drawFailure(ctx, camera, options);
}
